/**
 * AMT (Antragsmanager) Moddle Descriptor
 *
 * Defines the custom AMT namespace for BPMN extensions used in the Antragsmanager.
 * These extensions include:
 * - taskDisplay: UI metadata for tasks (labels, descriptions, priority, icon, color)
 * - formDefinition: Dynamic form definitions with fields and validation
 * - llmContext: AI/LLM assistance configuration
 * - taskAssignment: Task assignment rules
 */

export const AMT_NAMESPACE = 'http://gov.de/antragsmanager/bpmn';
export const AMT_PREFIX = 'amt';

export interface AmtTaskDisplay {
  labels: Record<string, string>;
  descriptions: Record<string, string>;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  icon?: string;
  color?: string;
}

export interface AmtFormField {
  id: string;
  type: FieldType;
  processVariable: string;
  labels: Record<string, string>;
  placeholders: Record<string, string>;
  hints: Record<string, string>;
  required: boolean;
  readOnly: boolean;
  defaultValue?: string;
  options: Array<{ value: string; labels: Record<string, string> }>;
  validationRules: Array<{
    type: ValidationRuleType;
    value?: string;
    messages: Record<string, string>;
  }>;
  conditional?: {
    field: string;
    operator: 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'GREATER_THAN' | 'LESS_THAN';
    value: string;
  };
}

export interface AmtFormSection {
  id: string;
  titles: Record<string, string>;
  descriptions: Record<string, string>;
  fields: AmtFormField[];
  collapsible: boolean;
  collapsed: boolean;
}

export interface AmtFormDefinition {
  id: string;
  sections: AmtFormSection[];
}

export interface AmtLlmContext {
  enabled: boolean;
  assistanceType: 'GENERAL' | 'DECISION_SUPPORT' | 'DOCUMENT_ANALYSIS' | 'FORM_ASSISTANCE';
  systemPrompt?: string;
  includeVariables: Array<{ name: string; alias?: string; sensitive: boolean }>;
  excludeVariables: Array<{ name: string; alias?: string; sensitive: boolean }>;
}

export interface AmtTaskAssignment {
  allowUnclaim: boolean;
}

export interface AmtProcessConfig {
  returnChannelEnabled: boolean;
}

export type FieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'EMAIL'
  | 'PHONE'
  | 'DATE'
  | 'DATETIME'
  | 'CHECKBOX'
  | 'RADIO'
  | 'SELECT'
  | 'MULTISELECT'
  | 'FILE'
  | 'SIGNATURE'
  | 'IBAN'
  | 'ADDRESS'
  | 'RICH_TEXT';

export type ValidationRuleType =
  | 'MIN_LENGTH'
  | 'MAX_LENGTH'
  | 'MIN'
  | 'MAX'
  | 'PATTERN'
  | 'EMAIL'
  | 'PHONE'
  | 'IBAN'
  | 'FUTURE_DATE'
  | 'PAST_DATE'
  | 'FILE_TYPE'
  | 'FILE_SIZE';

export const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'TEXT', label: 'Textfeld' },
  { value: 'TEXTAREA', label: 'Mehrzeiliges Textfeld' },
  { value: 'NUMBER', label: 'Zahl' },
  { value: 'EMAIL', label: 'E-Mail' },
  { value: 'PHONE', label: 'Telefon' },
  { value: 'DATE', label: 'Datum' },
  { value: 'DATETIME', label: 'Datum und Uhrzeit' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'RADIO', label: 'Optionsfeld' },
  { value: 'SELECT', label: 'Auswahl' },
  { value: 'MULTISELECT', label: 'Mehrfachauswahl' },
  { value: 'FILE', label: 'Datei-Upload' },
  { value: 'SIGNATURE', label: 'Unterschrift' },
  { value: 'IBAN', label: 'IBAN' },
  { value: 'ADDRESS', label: 'Adresse' },
  { value: 'RICH_TEXT', label: 'Formatierter Text' },
];

export const PRIORITY_OPTIONS: { value: AmtTaskDisplay['priority']; label: string; color: string }[] = [
  { value: 'LOW', label: 'Niedrig', color: '#4caf50' },
  { value: 'MEDIUM', label: 'Mittel', color: '#ff9800' },
  { value: 'HIGH', label: 'Hoch', color: '#f44336' },
  { value: 'URGENT', label: 'Dringend', color: '#9c27b0' },
];

export const VALIDATION_RULE_TYPES: { value: ValidationRuleType; label: string; hasValue: boolean }[] = [
  { value: 'MIN_LENGTH', label: 'Mindestlaenge', hasValue: true },
  { value: 'MAX_LENGTH', label: 'Maximallaenge', hasValue: true },
  { value: 'MIN', label: 'Mindestwert', hasValue: true },
  { value: 'MAX', label: 'Maximalwert', hasValue: true },
  { value: 'PATTERN', label: 'Muster (RegEx)', hasValue: true },
  { value: 'EMAIL', label: 'E-Mail-Format', hasValue: false },
  { value: 'PHONE', label: 'Telefon-Format', hasValue: false },
  { value: 'IBAN', label: 'IBAN-Format', hasValue: false },
  { value: 'FUTURE_DATE', label: 'Zukuenftiges Datum', hasValue: false },
  { value: 'PAST_DATE', label: 'Vergangenes Datum', hasValue: false },
  { value: 'FILE_TYPE', label: 'Dateityp', hasValue: true },
  { value: 'FILE_SIZE', label: 'Max. Dateigroesse', hasValue: true },
];

/**
 * Default empty values for creating new extension elements
 */
export const createEmptyTaskDisplay = (): AmtTaskDisplay => ({
  labels: { de: '' },
  descriptions: { de: '' },
  priority: 'MEDIUM',
  icon: undefined,
  color: undefined,
});

export const createEmptyFormField = (): AmtFormField => ({
  id: `field_${Date.now()}`,
  type: 'TEXT',
  processVariable: '',
  labels: { de: '' },
  placeholders: { de: '' },
  hints: { de: '' },
  required: false,
  readOnly: false,
  defaultValue: undefined,
  options: [],
  validationRules: [],
  conditional: undefined,
});

export const createEmptyFormSection = (): AmtFormSection => ({
  id: `section_${Date.now()}`,
  titles: { de: '' },
  descriptions: { de: '' },
  fields: [],
  collapsible: false,
  collapsed: false,
});

export const createEmptyFormDefinition = (): AmtFormDefinition => ({
  id: `form_${Date.now()}`,
  sections: [createEmptyFormSection()],
});

export const createEmptyLlmContext = (): AmtLlmContext => ({
  enabled: false,
  assistanceType: 'GENERAL',
  systemPrompt: undefined,
  includeVariables: [],
  excludeVariables: [],
});

export const createEmptyTaskAssignment = (): AmtTaskAssignment => ({
  allowUnclaim: true,
});

export const createEmptyProcessConfig = (): AmtProcessConfig => ({
  returnChannelEnabled: true,
});

/**
 * AMT Moddle Descriptor for bpmn-js
 * This descriptor allows bpmn-js to understand and process AMT extension elements.
 */
export const amtModdleDescriptor = {
  name: 'AMT',
  uri: AMT_NAMESPACE,
  prefix: AMT_PREFIX,
  xml: {
    tagAlias: 'lowerCase',
  },
  types: [
    {
      name: 'TaskDisplay',
      superClass: ['Element'],
      properties: [
        {
          name: 'labels',
          isMany: true,
          type: 'Label',
        },
        {
          name: 'descriptions',
          isMany: true,
          type: 'Description',
        },
        {
          name: 'priority',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'icon',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'color',
          isAttr: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'FormDefinition',
      superClass: ['Element'],
      properties: [
        {
          name: 'id',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'sections',
          isMany: true,
          type: 'Section',
        },
      ],
    },
    {
      name: 'Section',
      superClass: ['Element'],
      properties: [
        {
          name: 'id',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'collapsible',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'collapsed',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'titles',
          isMany: true,
          type: 'Title',
        },
        {
          name: 'descriptions',
          isMany: true,
          type: 'Description',
        },
        {
          name: 'fields',
          isMany: true,
          type: 'Field',
        },
      ],
    },
    {
      name: 'Field',
      superClass: ['Element'],
      properties: [
        {
          name: 'id',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'type',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'processVariable',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'required',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'readOnly',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'defaultValue',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'labels',
          isMany: true,
          type: 'Label',
        },
        {
          name: 'placeholders',
          isMany: true,
          type: 'Placeholder',
        },
        {
          name: 'hints',
          isMany: true,
          type: 'Hint',
        },
        {
          name: 'options',
          type: 'Options',
        },
        {
          name: 'validation',
          type: 'Validation',
        },
        {
          name: 'conditional',
          type: 'Conditional',
        },
      ],
    },
    {
      name: 'Label',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Title',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Description',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Placeholder',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Hint',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Options',
      superClass: ['Element'],
      properties: [
        {
          name: 'option',
          isMany: true,
          type: 'Option',
        },
      ],
    },
    {
      name: 'Option',
      superClass: ['Element'],
      properties: [
        {
          name: 'value',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'labels',
          isMany: true,
          type: 'Label',
        },
      ],
    },
    {
      name: 'Validation',
      superClass: ['Element'],
      properties: [
        {
          name: 'rules',
          isMany: true,
          type: 'Rule',
        },
      ],
    },
    {
      name: 'Rule',
      superClass: ['Element'],
      properties: [
        {
          name: 'type',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'messages',
          isMany: true,
          type: 'Message',
        },
      ],
    },
    {
      name: 'Message',
      superClass: ['Element'],
      properties: [
        {
          name: 'lang',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Conditional',
      superClass: ['Element'],
      properties: [
        {
          name: 'field',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'operator',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'value',
          isAttr: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'LlmContext',
      superClass: ['Element'],
      properties: [
        {
          name: 'enabled',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'assistanceType',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'systemPrompt',
          type: 'SystemPrompt',
        },
        {
          name: 'includeVariables',
          type: 'IncludeVariables',
        },
        {
          name: 'excludeVariables',
          type: 'ExcludeVariables',
        },
      ],
    },
    {
      name: 'SystemPrompt',
      superClass: ['Element'],
      properties: [
        {
          name: 'value',
          isBody: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'IncludeVariables',
      superClass: ['Element'],
      properties: [
        {
          name: 'variables',
          isMany: true,
          type: 'Variable',
        },
      ],
    },
    {
      name: 'ExcludeVariables',
      superClass: ['Element'],
      properties: [
        {
          name: 'variables',
          isMany: true,
          type: 'Variable',
        },
      ],
    },
    {
      name: 'Variable',
      superClass: ['Element'],
      properties: [
        {
          name: 'name',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'alias',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'sensitive',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    {
      name: 'TaskAssignment',
      superClass: ['Element'],
      properties: [
        {
          name: 'allowUnclaim',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    {
      name: 'ProcessConfig',
      superClass: ['Element'],
      properties: [
        {
          name: 'returnChannelEnabled',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
  ],
};
